import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'

import { ActionMenu } from '../ui/ActionMenu'
import { Badge } from '../ui/Badge'
import type { DonationRecord } from '../../services/allodons'
import { formatCurrency, formatDate } from '../../utils/format'
import { DonationReconciliationBadge, DonationSourceBadge, paymentMethodLabel, recordedByLabel } from './DonationStatusBadges'

/**
 * LES DERNIERS DONS, une ligne chacun — date, montant, « Récurrent », moyen de
 * paiement, campagne / catégorie quand AlloDons les donne.
 *
 * Extrait du panneau « Numéro inconnu » (LOT-NUMÉRO-INCONNU) par
 * LOT-DONS-AGENT-1 pour servir aussi la fiche contact et l'onglet Dons de la
 * scène : un seul rendu de ligne de don dans les surfaces compactes.
 *
 * Date et montant sont INSÉCABLES (`whitespace-nowrap`, `shrink-0`) : à 768 px
 * c'est le libellé de campagne qui se tronque, jamais un montant coupé en deux
 * (registre §5, « un numéro est whitespace-nowrap, son bloc shrink-0 »).
 * Clés : `contacts:donations.*` (préfixe explicite, composant partagé entre
 * namespaces — registre §2).
 *
 * #253 — l'ASSOCIATION du don (étiquette) quand l'organisation en a plusieurs :
 * le serveur ne rend `associationLabel` que s'il y a à distinguer. Comme
 * le contexte, c'est elle qui se tronque, jamais le montant.
 *
 * DONS-MANUELS-B (#403) — un don `MANUAL` porte « Saisi à la main » et son
 * moyen de paiement traduit (liste fermée) ; un couple en attente porte « À
 * rapprocher » ; un doublon confirmé (`RECONCILED`) porte « Doublon confirmé »
 * et la ligne est ATTÉNUÉE (non comptée : le résumé du serveur l'exclut déjà).
 * `onDelete` + `canDelete` (facultatifs — l'onglet Dons de la scène ne les
 * passe pas) : menu d'actions à libellé « Supprimer » sur les lignes que la
 * session peut retirer (design system §5 : jamais une icône rouge nue).
 */
export function RecentDonationsList({
  items,
  className = '',
  canDelete,
  onDelete,
  currentUserId = null,
}: {
  items: DonationRecord[]
  className?: string
  canDelete?: (donation: DonationRecord) => boolean
  onDelete?: (donation: DonationRecord) => void
  /** Session : « Saisi par vous » sur ses propres dons manuels (sinon le nom rendu par le serveur). */
  currentUserId?: string | null
}) {
  const { t } = useTranslation('contacts')
  if (items.length === 0) return null
  return (
    <ul className={`divide-y divide-border ${className}`.trim()}>
      {items.map((donation) => {
        const context = [donation.campaignName, donation.category].filter(Boolean).join(' · ')
        const method = paymentMethodLabel(t, donation)
        const author = recordedByLabel(t, donation, currentUserId)
        const dimmed = donation.source === 'MANUAL' && donation.reconciliationStatus === 'RECONCILED'
        const deletable = Boolean(onDelete && canDelete?.(donation))
        return (
          <li
            key={donation.id}
            data-donation-id={donation.id}
            data-dimmed={dimmed ? 'true' : undefined}
            className={`flex min-h-touch min-w-0 flex-wrap items-center gap-x-3 gap-y-1 py-2${dimmed ? ' opacity-60' : ''}`}
          >
            <span className="shrink-0 whitespace-nowrap tabular-nums text-t5 text-foreground-secondary">
              {formatDate(donation.donationDate)}
            </span>
            <span className="shrink-0 whitespace-nowrap tabular-nums text-t5 font-semibold text-foreground">
              {formatCurrency(Number(donation.amount), donation.currency)}
            </span>
            {donation.isRecurring && <Badge>{t('contacts:donations.recurring')}</Badge>}
            <DonationSourceBadge donation={donation} />
            <DonationReconciliationBadge donation={donation} />
            {donation.associationLabel && (
              <span
                className="min-w-0 max-w-[12rem] truncate"
                title={t('contacts:donations.association', { name: donation.associationLabel })}
                data-testid="donation-association"
              >
                <Badge tone="accent">{donation.associationLabel}</Badge>
              </span>
            )}
            {method && <span className="shrink-0 text-t5 text-foreground-secondary">{method}</span>}
            {author && (
              <span className="shrink-0 text-t5 text-foreground-tertiary" data-testid="donation-author">
                {author}
              </span>
            )}
            {context && (
              <span className="min-w-0 flex-1 truncate text-t5 text-foreground-secondary" title={context}>
                {context}
              </span>
            )}
            {deletable && (
              <span className="ml-auto shrink-0">
                <ActionMenu
                  label={t('donations:manual.delete.menu')}
                  actions={[
                    {
                      id: 'delete',
                      label: t('donations:manual.delete.action'),
                      icon: Trash2,
                      destructive: true,
                      onClick: () => onDelete?.(donation),
                    },
                  ]}
                />
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

export default RecentDonationsList
